import { Icon } from '../Icon'

/**
 * Строка списка диалогов.
 *
 * Композиция строки, а не карточка: список диалогов — рабочий
 * инструмент, десятки строк на экране консоли. Группа отличается
 * от личного диалога знаком слева (иконка группы против инициалов
 * собеседника) — подписи «группа/личный» не нужны.
 *
 * Непрочитанное — мятная капсула; заглушённый диалог показывает
 * перечёркнутый колокольчик и глушит капсулу до нейтральной:
 * заглушено — значит «не дёргай», в том числе цветом.
 */

function initials(name = '') {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '·'
}

export function ConversationListItem({
  conversation,
  title,
  subtitle = '',            // последнее сообщение, готовой строкой
  timeLabel = '',
  active = false,
  muted = false,
  onClick,
}) {
  const isGroup = conversation.kind === 'group'
  const unread = conversation.unread_count || 0

  return (
    <button
      type="button"
      className={[
        'conv',
        active ? 'conv--active' : '',
        unread > 0 && !muted ? 'conv--unread' : '',
      ].join(' ').trim()}
      onClick={onClick}
    >
      <span className={`conv__mark ${isGroup ? 'conv__mark--group' : ''}`} aria-hidden>
        {isGroup ? <Icon name="users" size={20} /> : initials(title)}
      </span>

      <span className="conv__main">
        <span className="conv__top">
          <span className="conv__title">{title}</span>
          {timeLabel && <span className="conv__time">{timeLabel}</span>}
        </span>
        <span className="conv__bottom">
          <span className="conv__preview">{subtitle || 'Нет сообщений'}</span>
          {muted && (
            <span className="conv__muted" title="Уведомления выключены">
              <Icon name="bellOff" size={16} />
            </span>
          )}
          {unread > 0 && (
            <span className={`conv__badge ${muted ? 'conv__badge--muted' : ''}`}>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </span>
      </span>
    </button>
  )
}

/**
 * Миниатюра вложения.
 *
 * Контейнер фиксированный — лента не прыгает, пока грузится превью.
 * Состояния: загрузка (скелет), ошибка (заглушка с контуром файла),
 * готово. PDF и прочие не-картинки сразу показывают файл-заглушку
 * с расширением.
 */
export function AttachmentThumb({
  url = null,              // presigned-ссылка миниатюры; null — грузится
  mime = 'image/jpeg',
  error = false,
  label = '',              // подпись для не-картинок, напр. «PDF»
  onClick,
}) {
  const isImage = mime.startsWith('image/')

  let inner
  if (error) {
    inner = (
      <span className="thumb__fallback" title="Не удалось загрузить">
        <Icon name="warning" size={20} />
      </span>
    )
  } else if (!isImage) {
    inner = (
      <span className="thumb__fallback">
        <Icon name="file" size={20} />
        {label && <span className="thumb__ext">{label}</span>}
      </span>
    )
  } else if (!url) {
    inner = <span className="thumb__skeleton skeleton" aria-label="Загрузка" />
  } else {
    inner = <img className="thumb__img" src={url} alt="Вложение" loading="lazy" />
  }

  return (
    <button
      type="button"
      className="thumb"
      onClick={onClick}
      disabled={!onClick || error || (!url && isImage)}
      aria-label="Открыть вложение"
    >
      {inner}
    </button>
  )
}

/**
 * Полоса закреплённого сообщения над лентой — «важный момент» группы.
 *
 * Одна строка с обрезкой: закреп — указатель, не читальня;
 * нажатие ведёт к сообщению. Открепление — только когда передан
 * обработчик (право владельца решает экран).
 */
export function PinnedBar({ pinned, onOpen, onUnpin }) {
  if (!pinned) return null
  const text = pinned.message?.deleted
    ? 'Сообщение удалено'
    : pinned.message?.body || 'Вложение'

  return (
    <div className="pinned">
      <span className="pinned__icon" aria-hidden>
        <Icon name="pin" size={16} />
      </span>
      <button type="button" className="pinned__text" onClick={onOpen}>
        {text}
      </button>
      {onUnpin && (
        <button
          type="button"
          className="pinned__unpin"
          onClick={onUnpin}
          aria-label="Открепить"
        >
          <Icon name="close" size={16} />
        </button>
      )}
    </div>
  )
}
